import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import Button from '../../Common/Button';
import arrowDown from '../../../../assets/img/svg/sidebar/timezone-arrow-down.svg';
import { useDateFilter } from '../../../../hook/useDateFilter';
import { useAppSelector } from '../../../../hook/useAppSelector';
import { useAppDispatch } from '../../../../hook/useAppDispatch';
import { setRange, validateTimes } from '../../../../store/slice/date-slice';
import { DateRangeOption, TimeField } from '../../../../enum/DatePicker';
import { Visibility } from '../../../../enum/Visibility';

interface DateFilterProps {
  onSearch: (range: any) => void;
}

export const DateFilter: React.FC<DateFilterProps> = ({ onSearch }) => {
  const {
    preset,
    isOpen,
    isVisible,
    togglePicker,
    handlePreset,
    handleSelect,
    handleTimeChange,
    handleSearch,
    formatDate,
  } = useDateFilter(onSearch);
  const range = useAppSelector((state) => state.dateFilter);
  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const pickerRef = useRef<HTMLDivElement>(null);

  // Click outside component to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setTimeout(() => togglePicker(), 0);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, togglePicker]);

  const getSelectedRange = () => {
    if (range.from && range.to) {
      return { from: new Date(range.from), to: new Date(range.to) };
    }

    if (range.from) {
      return { from: new Date(range.from), to: new Date(range.from) };
    }

    return undefined;
  };

  const renderPresetButtons = () => {
    const presets = Object.values(DateRangeOption);

    return (
      <div className="preset_buttons">
        {presets.map((p) => {
          const label = t(`dataPicker.presets.${p}`);

          return (
            <button key={p} className={`preset_btn ${preset === p ? 'active' : ''}`} onClick={() => handlePreset(p)}>
              {label}
            </button>
          );
        })}
      </div>
    );
  };

  // Close after search
  const handleSearchClick = () => {
    handleSearch();
    togglePicker();
  };

  return (
    <div className="date_picker_container" ref={pickerRef}>
      <Button iconRight={arrowDown} className="date_picker_button" onClick={togglePicker}>
        <p className="selected_range">
          {range?.from ? formatDate(new Date(range.from)) : ''} – {range?.to ? formatDate(new Date(range.to)) : ''}
        </p>
      </Button>

      {isOpen && (
        <div className={`date_filter ${isVisible ? Visibility.VISIBLE : ''}`}>
          <div className="calendar_container">
            <div className="time_inputs">
              {/* Start time */}
              <label>
                {t('dataPicker.start')}
                <div className="time_input">
                  <input
                    type="text"
                    maxLength={2}
                    placeholder="00"
                    value={range.timeFromHour || ''}
                    onChange={(e) => handleTimeChange(TimeField.FROM, e.target.value)}
                    onFocus={() => dispatch(setRange({ timeFromHour: '' }))}
                    onBlur={() => {
                      const val = range.timeFromHour || '';
                      if (val !== '') {
                        dispatch(
                          setRange({
                            timeFromHour: val.padStart(2, '0'),
                          }),
                        );
                      }
                      dispatch(validateTimes());
                    }}
                  />
                  <span>:</span>
                  <input type="text" value="00" disabled />
                </div>
              </label>

              <div className="time_divider"></div>

              {/* End time */}
              <label>
                {t('dataPicker.end')}
                <div className="time_input">
                  <input
                    type="text"
                    maxLength={2}
                    placeholder="00"
                    value={range.timeToHour || ''}
                    onChange={(e) => handleTimeChange(TimeField.TO, e.target.value)}
                    onFocus={() => dispatch(setRange({ timeToHour: '' }))}
                    onBlur={() => {
                      const val = range.timeToHour || '';
                      if (val !== '') {
                        dispatch(
                          setRange({
                            timeToHour: val.padStart(2, '0'),
                          }),
                        );
                      }
                      dispatch(validateTimes());
                    }}
                  /> <span>:</span> <input type="text" value="00" disabled />
                </div>
              </label>
            </div>

            <DayPicker
              mode="range"
              selected={getSelectedRange()}
              onSelect={handleSelect}
              disabled={[{ after: new Date() }]}
            />
          </div>

          <div className="actions">
            {renderPresetButtons()}

            <button className="search_btn" onClick={handleSearchClick}>
              {t('dataPicker.search')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
